import { useState } from 'react';
import type { ReactNode } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import clsx from 'clsx';
import { MdCalendarToday } from 'react-icons/md';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTrigger,
  AlertDialogOverlay,
  AlertDialogPortal,
} from './ui/alert-dialog';
import './DatePickerModal.scss';

interface DatePickerModalProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  type?: 'date' | 'datetime';
  children?: ReactNode;
}

const renderCustomHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  changeYear,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  changeYear: (year: number) => void;
}) => {
  const month = new Intl.DateTimeFormat('ja', { month: 'long' }).format(date);

  const currentYear = date.getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 80; i <= currentYear + 20; i++) {
    yearOptions.unshift(i);
  }

  return (
    <div className="flex items-center justify-between px-2">
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          decreaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-left.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
      <div className="flex items-center gap-2">
        <select
          value={currentYear}
          onChange={(e) => {
            e.stopPropagation();
            changeYear(parseInt(e.target.value));
          }}
          className="cursor-pointer rounded border-none bg-transparent px-1 text-base font-bold text-sub outline-hidden hover:bg-gray-100"
        >
          {yearOptions.map((yearOption) => (
            <option key={yearOption} value={yearOption}>
              {yearOption}
            </option>
          ))}
        </select>
        <span className="text-base font-bold text-sub">{month}</span>
      </div>
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          increaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-right.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
    </div>
  );
};

const renderCustomHeaderDatetime = ({
  date,
  customHeaderCount,
  decreaseMonth,
  increaseMonth,
}: {
  date: Date;
  customHeaderCount: number;
  decreaseMonth: () => void;
  increaseMonth: () => void;
}) => {
  const adjustedDate = new Date(date);
  adjustedDate.setMonth(adjustedDate.getMonth() + customHeaderCount);

  const year = new Intl.DateTimeFormat('ja', { year: 'numeric' }).format(adjustedDate);
  const month = new Intl.DateTimeFormat('ja', { month: 'long' }).format(adjustedDate);

  return (
    <div className="flex justify-between px-2">
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          decreaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-left.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
      <span className="text-base font-bold text-sub">{` ${year}${month} `}</span>
      <button
        type="button"
        className="size-lg"
        onClick={(e) => {
          e.stopPropagation();
          increaseMonth();
        }}
      >
        <img
          src="/agent/job_change_processes/icon-chevron-right.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>
    </div>
  );
};

export default function DatePickerModal({
  value,
  onChange,
  placeholder = '日付を選択',
  className = '',
  type = 'date',
  children,
}: DatePickerModalProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | undefined>();
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');

  const currentDate = value ? new Date(value) : undefined;

  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateToISOString = (date: Date): string => {
    return date.toISOString();
  };

  const formatDisplayDate = (dateString: string): string => {
    if (!dateString) return '';
    return dateString.replace(/-/g, '/');
  };

  const formatDisplayDatetime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const handleDateChange = (date: Date | null) => {
    setTempSelectedDate(date || undefined);
  };

  const handleSubmit = () => {
    if (tempSelectedDate) {
      if (type === 'datetime') {
        const updatedDate = new Date(tempSelectedDate);
        updatedDate.setHours(Number(selectedHour));
        updatedDate.setMinutes(Number(selectedMinute));
        const dateString = dateToISOString(updatedDate);
        onChange(dateString);
      } else {
        const dateString = dateToString(tempSelectedDate);
        onChange(dateString);
      }
    }
    setIsModalOpen(false);
    setTempSelectedDate(undefined);
    setSelectedHour('');
    setSelectedMinute('');
  };

  const handleClear = () => {
    onChange('');
    setIsModalOpen(false);
    setTempSelectedDate(undefined);
    setSelectedHour('');
    setSelectedMinute('');
  };

  // モーダルの開閉に連動して初期化する。
  // Radix の Trigger 経由（カスタムトリガー含む）でも必ず呼ばれるよう、
  // トリガーの onClick ではなく onOpenChange で初期化する。
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTempSelectedDate(currentDate || new Date());
      if (type === 'datetime') {
        if (currentDate) {
          setSelectedHour(String(currentDate.getHours()).padStart(2, '0'));
          setSelectedMinute(String(currentDate.getMinutes()).padStart(2, '0'));
        } else {
          // 既存日時がない場合でも 00:00 を明示的に選択済みにしておく
          // （未選択のまま「決定」して意図せず 00:00 送信になるのを避ける）
          setSelectedHour('00');
          setSelectedMinute('00');
        }
      }
    }
    setIsModalOpen(open);
  };

  const displayText =
    type === 'datetime'
      ? value
        ? formatDisplayDatetime(value)
        : placeholder
      : value
        ? formatDisplayDate(value)
        : placeholder;

  const defaultTrigger = (
    <button
      type="button"
      className={clsx(
        'flex h-[42px] w-full items-center rounded border border-solid border-border bg-white px-3 focus-visible:border-2 focus-visible:border-primary focus-visible:outline-hidden',
        className
      )}
    >
      <span className={clsx('text-sm', !value && 'text-gray-500')}>{displayText}</span>
      <MdCalendarToday className="ml-auto size-5 text-gray-400" />
    </button>
  );

  return (
    <AlertDialog open={isModalOpen} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>{children || defaultTrigger}</AlertDialogTrigger>
      <AlertDialogPortal>
        <AlertDialogOverlay className="bg-modal" />
        <AlertDialogContent
          className="w-auto max-w-max border-none p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="rounded-lg border-2 border-solid border-border bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]"
            onClick={(e) => e.stopPropagation()}
          >
            {type === 'datetime' ? (
              <div className="flex max-h-[266.83px]">
                <div className="w-[236px] border-r border-b border-solid border-border">
                  <DatePicker
                    selected={tempSelectedDate}
                    dateFormat="yyyy年MM月dd日"
                    locale={'ja'}
                    inline
                    onChange={handleDateChange}
                    calendarClassName="calendar-datetime-menu"
                    renderCustomHeader={(params) => {
                      return renderCustomHeaderDatetime({
                        date: params.date,
                        customHeaderCount: params.customHeaderCount,
                        decreaseMonth: params.decreaseMonth,
                        increaseMonth: params.increaseMonth,
                      });
                    }}
                  />
                </div>
                <div className="flex max-h-[266.83px] overflow-hidden border-b border-solid border-border">
                  <div className="hidden-scrollbar flex h-[266.83px] w-[44px] flex-col items-center gap-2 overflow-y-auto border-r border-solid border-border bg-white">
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = String(i).padStart(2, '0');
                      return (
                        <div
                          key={`hour-${i}`}
                          className={clsx(
                            'flex size-xl min-h-xl cursor-pointer items-center justify-center hover:rounded-[100px] hover:bg-[#EEEEEE]',
                            selectedHour === hour &&
                              'rounded-[100px] bg-primary text-white hover:bg-primary'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedHour(hour);
                          }}
                        >
                          {hour}
                        </div>
                      );
                    })}
                  </div>
                  <div className="hidden-scrollbar flex h-[266.83px] w-[44px] flex-col items-center gap-2 overflow-y-auto bg-white">
                    {Array.from({ length: 12 }, (_, i) => {
                      const minutes = String(i * 5).padStart(2, '0');
                      return (
                        <div
                          key={`minutes-${i}`}
                          className={clsx(
                            'flex size-xl min-h-xl cursor-pointer items-center justify-center hover:rounded-[100px] hover:bg-[#EEEEEE]',
                            selectedMinute === minutes &&
                              'rounded-[100px] bg-primary text-white hover:bg-primary'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMinute(minutes);
                          }}
                        >
                          {minutes}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[266.83px] w-[236px] overflow-hidden border-b border-solid border-border">
                <DatePicker
                  selected={tempSelectedDate}
                  dateFormat="yyyy年MM月dd日"
                  locale={'ja'}
                  inline
                  onChange={handleDateChange}
                  calendarClassName="calendar-datetime-menu"
                  renderCustomHeader={(params) => {
                    return renderCustomHeader({
                      date: params.date,
                      decreaseMonth: params.decreaseMonth,
                      increaseMonth: params.increaseMonth,
                      changeYear: params.changeYear,
                    });
                  }}
                />
              </div>
            )}
            <div className="flex h-[56px] items-center justify-end gap-1 bg-white px-3">
              <button
                type="button"
                className="h-8 w-[52px] rounded border border-solid border-border bg-white text-sm font-bold text-sub hover:bg-gray-50"
                onClick={handleClear}
              >
                クリア
              </button>
              <button
                type="button"
                className="hover:bg-primary-dark h-8 w-[72px] rounded bg-primary text-sm font-bold text-white"
                onClick={handleSubmit}
              >
                決定
              </button>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
